import { headerKeys } from "@/config/general";
import {
  NOT_LOGGED_IN_ERROR_STATUS,
  OK_STATUS,
  parseFrontendError,
} from "@/fe-core/helpers/general";
import { IJsonResponse } from "@/fe-core/meta/interfaces/root";
import {
  IOptInOutJackpotResponse,
  OptInJackpotRequestAction,
  optInOutJackpotActionTypes,
  OptOutJackpotRequestAction,
} from "@/fe-core/meta/types/optInOutJackpot";
import { methodTypes } from "@/fe-core/meta/types/root";
import {
  all,
  AllEffect,
  delay,
  fork,
  ForkEffect,
  put,
  select,
  takeLatest,
} from "redux-saga/effects";
import { getEligibleJackpotsRequest } from "../actions/eligibleJackpotsActions";
import {
  clearOptInOutJackpotMessage,
  optInJackpotFailure,
  optInJackpotSuccess,
  optOutJackpotFailure,
  optOutJackpotSuccess,
} from "../actions/optInOutJackpotActions";
import { clearState } from "../actions/rootActions";
import { sessionDataSelector } from "../selectors/sessionSelectors";

function* clearOptInOutJackpotMessageSaga(interval: number) {
  yield delay(interval);
  yield put(clearOptInOutJackpotMessage());
}

function* optInJackpotSaga({ payload }: OptInJackpotRequestAction) {
  try {
    const { sessionId } = yield select(sessionDataSelector);

    const response: IJsonResponse<IOptInOutJackpotResponse> = yield fetch(
      `/api/optInJackpot`,
      {
        method: methodTypes.POST,
        headers: new Headers({
          [headerKeys.SESSION_ID]: sessionId,
        }),
        body: JSON.stringify({
          jackpotId: payload,
        }),
      }
    );
    const { status } = response;
    const responsePayload: IOptInOutJackpotResponse = yield response.json();

    if (responsePayload?.status === OK_STATUS && status === 200) {
      yield put(optInJackpotSuccess({ message: responsePayload?.message }));
      yield put(getEligibleJackpotsRequest());
    } else if (
      responsePayload?.status === NOT_LOGGED_IN_ERROR_STATUS ||
      status === 401
    ) {
      yield put(optInJackpotFailure({ message: responsePayload?.status }));
      yield put(clearState());
    } else {
      yield put(optInJackpotFailure({ message: responsePayload?.status }));
    }
  } catch (error) {
    yield put(optInJackpotFailure(parseFrontendError(error)));
  } finally {
    yield fork(clearOptInOutJackpotMessageSaga, 5000);
  }
}

function* optOutJackpotSaga({ payload }: OptOutJackpotRequestAction) {
  try {
    const { sessionId } = yield select(sessionDataSelector);

    const response: IJsonResponse<IOptInOutJackpotResponse> = yield fetch(
      `/api/optOutJackpot`,
      {
        method: methodTypes.POST,
        headers: new Headers({
          [headerKeys.SESSION_ID]: sessionId,
        }),
        body: JSON.stringify({
          jackpotId: payload,
        }),
      }
    );
    const { status } = response;
    const responsePayload: IOptInOutJackpotResponse = yield response.json();

    if (responsePayload?.status === OK_STATUS && status === 200) {
      yield put(optOutJackpotSuccess({ message: responsePayload?.message }));
      yield put(getEligibleJackpotsRequest());
    } else if (
      responsePayload?.status === NOT_LOGGED_IN_ERROR_STATUS ||
      status === 401
    ) {
      yield put(optOutJackpotFailure({ message: responsePayload?.status }));
      yield put(clearState());
    } else {
      yield put(optOutJackpotFailure({ message: responsePayload?.status }));
    }
  } catch (error) {
    yield put(optOutJackpotFailure(parseFrontendError(error)));
  } finally {
    yield fork(clearOptInOutJackpotMessageSaga, 5000);
  }
}

function* optInOutJackpotSaga(): Generator<
  AllEffect<ForkEffect<never>>,
  void,
  unknown
> {
  yield all([
    takeLatest(
      optInOutJackpotActionTypes.OPT_IN_JACKPOT_REQUEST,
      optInJackpotSaga
    ),
    takeLatest(
      optInOutJackpotActionTypes.OPT_OUT_JACKPOT_REQUEST,
      optOutJackpotSaga
    ),
  ]);
}

export default optInOutJackpotSaga;
